import React from 'react'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Card from '@mui/material/Card'
import CardContent from '@mui/material/CardContent'
import Chip from '@mui/material/Chip'
import FormControl from '@mui/material/FormControl'
import Grid from '@mui/material/Grid'
import InputLabel from '@mui/material/InputLabel'
import MenuItem from '@mui/material/MenuItem'
import Select from '@mui/material/Select'
import Typography from '@mui/material/Typography'
import { Link, router } from '@inertiajs/react'
import { AppLayout } from '@/components/layouts/AppLayout'

interface NotebookSummary {
  id: number
  name: string
  slug: string
  description: string | null
  project_id: number
  project_name: string
  note_count: number
  member_count: number
  my_role: 'admin' | 'editor' | 'reader' | null
  created_at: string
}

interface ProjectRef {
  id: number
  name: string
}

interface AgentRef {
  id: number
  username: string
}

interface Filters {
  project_id: number | null
  agent_id: number | null
  not_agent_id: number | null
}

interface Props {
  notebooks: NotebookSummary[]
  projects: ProjectRef[]
  agents: AgentRef[]
  filters: Filters
}

export default function Index({ notebooks, projects, agents, filters }: Props) {
  function applyFilter(key: string, value: any) {
    const newFilters: Record<string, any> = { ...filters, [key]: value || undefined }
    Object.keys(newFilters).forEach(k => {
      if (newFilters[k] === null || newFilters[k] === undefined || newFilters[k] === '' || newFilters[k] === 0) {
        delete newFilters[k]
      }
    })
    router.get('/notebooks/notebooks', newFilters, { preserveState: true })
  }

  const hasActiveFilters = filters.project_id || filters.agent_id || filters.not_agent_id

  return (
    <AppLayout
      title="Notebooks"
      action={
        <Button component={Link} href="/notebooks/notebooks/new" variant="contained">
          New notebook
        </Button>
      }
    >
      {/* Filter bar */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2, flexWrap: 'wrap' }}>
        <FormControl size="small" sx={{ minWidth: 140 }}>
          <InputLabel>Project</InputLabel>
          <Select
            value={filters.project_id || ''}
            label="Project"
            onChange={e => applyFilter('project_id', e.target.value || null)}
          >
            <MenuItem value="">All Projects</MenuItem>
            {projects.map((p) => (
              <MenuItem key={p.id} value={p.id}>{p.name}</MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel>Agent Assigned</InputLabel>
          <Select
            value={filters.agent_id || ''}
            label="Agent Assigned"
            onChange={e => applyFilter('agent_id', e.target.value || null)}
          >
            <MenuItem value="">Any Agent</MenuItem>
            {agents.map((a) => (
              <MenuItem key={a.id} value={a.id}>{a.username}</MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel>Agent Not Assigned</InputLabel>
          <Select
            value={filters.not_agent_id || ''}
            label="Agent Not Assigned"
            onChange={e => applyFilter('not_agent_id', e.target.value || null)}
          >
            <MenuItem value="">Any</MenuItem>
            {agents.map((a) => (
              <MenuItem key={a.id} value={a.id}>{a.username}</MenuItem>
            ))}
          </Select>
        </FormControl>

        {hasActiveFilters && (
          <Button
            size="small"
            onClick={() => router.get('/notebooks/notebooks', {}, { preserveState: true })}
          >
            Clear
          </Button>
        )}
      </Box>

      {notebooks.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 8 }}>
          No notebooks found.{!hasActiveFilters && ' Create your first notebook to get started.'}
        </Typography>
      ) : (
        <Grid container spacing={2}>
          {notebooks.map((notebook) => (
            <Grid size={{ xs: 12, sm: 6, md: 4, lg: 3, xl: 2.4 }} key={notebook.id}>
              <Card
                component={Link}
                href={`/notebooks/notebooks/${notebook.slug}`}
                elevation={0}
                sx={{
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 2,
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  textDecoration: 'none',
                  color: 'inherit',
                  cursor: 'pointer',
                  '&:hover': {
                    borderColor: 'text.secondary',
                    backgroundColor: 'grey.50',
                  },
                }}
              >
                <CardContent sx={{ flexGrow: 1 }}>
                  <Typography variant="h3" sx={{ mb: 0.5 }}>
                    {notebook.name}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                    {notebook.project_name}
                  </Typography>
                  {notebook.description && (
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                      {notebook.description}
                    </Typography>
                  )}
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    <Chip
                      label={`${notebook.note_count} note${notebook.note_count !== 1 ? 's' : ''}`}
                      size="small"
                      variant="outlined"
                    />
                    <Chip
                      label={`${notebook.member_count} member${notebook.member_count !== 1 ? 's' : ''}`}
                      size="small"
                      variant="outlined"
                    />
                    {notebook.my_role && (
                      <Chip
                        label={notebook.my_role}
                        size="small"
                        color={notebook.my_role === 'admin' ? 'secondary' : notebook.my_role === 'editor' ? 'primary' : 'default'}
                        variant="outlined"
                      />
                    )}
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}
    </AppLayout>
  )
}
